import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { getEtherscanLink, shortenHash } from '../../../utils/web3-utils'
import Address from '../../general/Address'
import useTaskData from '../../../hooks/useTaskData'
import LongId from '../../LongId'
import useTaskMetadata from '../../../hooks/useTaskMetadata'
import useTaskPlanId from '../../../hooks/useTaskPlanId'
import Network from '../../general/Network'
import useSimulateExecution from '../../../hooks/useSimulateExecution'
import { TaskExecutions } from '../../../types/API'

interface LogPanelProps {
  open: boolean
  onClose: () => void
  item: TaskExecutions
}

const statusColors: Record<string, { bg: string; text: string }> = {
  notSimulated:          { bg: 'rgba(239,64,111,0.2)',    text: '#ff6b8a' },
  simulationFailed:      { bg: 'rgba(211,133,22,0.2)',    text: '#d38516' },
  simulationReverted:    { bg: 'rgba(128,0,128,0.18)',    text: '#c06bc0' },
  simulationSucceeded:   { bg: 'rgba(210,211,22,0.18)',   text: '#d2d316' },
  executionDelayed:      { bg: 'rgba(22,127,211,0.2)',    text: '#5eb3f5' },
  executionSucceeded:    { bg: 'rgba(51,194,176,0.18)',   text: '#33C2B0' },
  executionReverted:     { bg: 'rgba(125,35,85,0.22)',    text: '#c06b9a' },
  executionNotReached:   { bg: 'rgba(197,127,66,0.22)',   text: '#d08040' },
  transactionReverted:   { bg: 'rgba(222,0,0,0.2)',       text: '#ff4444' },
}

const LogPanel: React.FC<LogPanelProps> = ({ open, onClose, item }) => {
  const { data: dataMeta } = useTaskMetadata(item?.id, open)
  const { data: dataPlan } = useTaskPlanId(item?.id, open)
  const { data, isLoading, simulateExecution } = useSimulateExecution(item?.id)

  return (
    <Detail isopen={open.toString()}>
      <div className="overlay" onClick={onClose} />
      <div
        className="wrap"
        onClick={(e) => e.target instanceof HTMLElement && e.target.tagName.toLowerCase() === 'a' && onClose()}
      >
        <PanelTitle>Task details</PanelTitle>

        <Row>
          <RowLabel>Task</RowLabel>
          <RowValue>{item?.task?.name}</RowValue>
        </Row>

        <Row>
          <RowLabel>Status</RowLabel>
          <RowValue>
            <StatusBadge status={item?.status?.type}>{item?.status?.type}</StatusBadge>
          </RowValue>
        </Row>

        <Row>
          {data ? (
            <>
              <RowLabel>Simulation</RowLabel>
              <SimLink href={data.simulationUrl} target="_blank" rel="noreferrer">
                Open Tenderly →
              </SimLink>
            </>
          ) : (
            <SimBtn onClick={() => simulateExecution()} disabled={isLoading}>
              {isLoading ? 'Simulating…' : 'Simulate Execution'}
            </SimBtn>
          )}
        </Row>

        <Row>
          <RowLabel>Date</RowLabel>
          <RowValue>{moment(item?.executedAt).format('MMM Do HH:mm:ss')}</RowValue>
        </Row>

        <Row>
          <RowLabel>Block</RowLabel>
          <Mono>{dataPlan?.blockNumber}</Mono>
        </Row>

        <Row>
          <RowLabel>Chain</RowLabel>
          <RowValue>
            <Network network={item?.chainId} small={false} noLogo={true} /> ({item?.chainId})
          </RowValue>
        </Row>

        <Row>
          <RowLabel>ID</RowLabel>
          <Mono small>{item?.id}</Mono>
        </Row>

        <Row>
          <RowLabel>Plan ID</RowLabel>
          <Mono small>{item?.planId}</Mono>
        </Row>

        <Row>
          <RowLabel>Task address</RowLabel>
          <Address address={item?.task?.address} chainId={item?.chainId} short />
        </Row>

        <Row>
          <RowLabel>Smart Vault</RowLabel>
          <Address address={item?.smartVault} chainId={item?.chainId} short showIdentity={false} />
        </Row>

        <Divider />

        <SectionTitle>Token In</SectionTitle>
        <TokenBlock>
          <LongId longId={item?.in?.token} />
          <TokenMeta>Amount: {item?.in?.amount}</TokenMeta>
          <TokenMeta>Price: ${item?.in?.amountUsd}</TokenMeta>
        </TokenBlock>

        {item?.out?.map((t, index) => (
          <React.Fragment key={index}>
            <SectionTitle>Token Out</SectionTitle>
            <TokenBlock>
              <LongId longId={t.token} />
              <TokenMeta>Amount: {t.amount}</TokenMeta>
              <TokenMeta>Price: ${t.amountUsd}</TokenMeta>
            </TokenBlock>
          </React.Fragment>
        ))}

        {(item?.status?.type === 'success' || item?.status?.type === 'reverted') && (
          <RenderTransactionInfo item={item} isOpen={open} />
        )}

        <Divider />

        <SectionTitle>Call Data</SectionTitle>
        <CodeBlock><LongId longId={item?.calldata} /></CodeBlock>

        <SectionTitle>Metadata</SectionTitle>
        <CodeBlock>{JSON.stringify(dataMeta, null, 2)}</CodeBlock>
      </div>
    </Detail>
  )
}

interface RenderTransactionInfoProps {
  item: { id: string; chainId?: number }
  isOpen: boolean
}

const RenderTransactionInfo: React.FC<RenderTransactionInfoProps> = ({ item, isOpen }) => {
  const { data } = useTaskData(item?.id, isOpen)

  return (
    <>
      <Divider />
      <SectionTitle>Transaction Info</SectionTitle>
      <Row>
        <RowLabel>Sender</RowLabel>
        <Address address={data?.sender || ''} chainId={data?.chainId} short />
      </Row>
      <Row>
        <RowLabel>Hash</RowLabel>
        <TxLink href={getEtherscanLink(data?.chainId || -1, data?.hash || '', 'transaction')} target="_blank" rel="noreferrer">
          {shortenHash(data?.hash || '')}
        </TxLink>
      </Row>
    </>
  )
}

export default LogPanel

export const LargeText = styled.p`
  font-size: 18px;
  font-weight: 400;
  line-height: 30px;
  color: #ededed;
  margin: 0;
`

export const StandardText = styled.p`
  font-size: 14px;
  line-height: 22px;
  color: #a0a0a0;
  margin: 0;
  word-break: break-word;
`

// ─── Styles ──────────────────────────────────────────────────────────────────

const Detail = styled.div<{ isopen: string }>`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 100;

  .overlay {
    content: '';
    display: block;
    position: absolute;
    left: 0; top: 0;
    width: 100%; height: 100%;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    will-change: opacity;
    pointer-events: none;
    transition: opacity 0.3s cubic-bezier(0, 0, 0.3, 1);
  }

  ${({ isopen }) => isopen === 'true' && '.overlay { opacity: 1; pointer-events: auto; }'}

  .wrap {
    position: fixed;
    box-sizing: border-box;
    height: 100%;
    width: 480px;
    padding: 32px 28px;
    background: #111;
    border-left: 1px solid rgba(255, 255, 255, 0.07);
    overflow-y: auto;
    overflow-x: hidden;
    transform: translateX(100%);
    will-change: transform;
    z-index: 101;
    pointer-events: auto;
    transition: transform 130ms ease-out;
    right: 0;

    @media only screen and (max-width: 510px) { width: 100%; }
  }

  ${({ isopen }) =>
    isopen === 'true' && '.wrap { transform: none; pointer-events: auto; transition: transform 330ms ease-in; }'}
`

const PanelTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: #ededed;
  margin: 0 0 24px;
`

const SectionTitle = styled.p`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #a1a1a1;
  margin: 16px 0 6px;
`

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child { border-bottom: none; }
`

const RowLabel = styled.span`
  font-size: 12px;
  color: #777;
  white-space: nowrap;
  flex-shrink: 0;
`

const RowValue = styled.span`
  font-size: 13px;
  color: #a0a0a0;
  text-align: right;
  word-break: break-all;
  display: flex;
  align-items: center;
  gap: 6px;
`

const Mono = styled.span<{ small?: boolean }>`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: ${({ small }) => small ? '10px' : '12px'};
  color: #777;
  word-break: break-all;
`

const StatusBadge = styled.span<{ status?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ status }) => statusColors[status!]?.bg || 'rgba(255,255,255,0.06)'};
  color: ${({ status }) => statusColors[status!]?.text || '#a0a0a0'};
`

const SimLink = styled.a`
  font-size: 13px;
  color: #a89aff;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`

const TxLink = styled.a`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 12px;
  color: #a89aff;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`

const SimBtn = styled.button`
  width: 100%;
  height: 34px;
  background: rgba(111, 92, 230, 0.1);
  border: 1px solid rgba(111, 92, 230, 0.3);
  border-radius: 6px;
  color: #a89aff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(:disabled) {
    background: rgba(111, 92, 230, 0.18);
    border-color: rgba(111, 92, 230, 0.5);
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 16px 0;
`

const TokenBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  background: #161616;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.06);
`

const TokenMeta = styled.span`
  font-size: 12px;
  color: #777;
`

const CodeBlock = styled.div`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 11px;
  color: #777;
  word-break: break-all;
  white-space: pre-wrap;
  background: #0d0d0d;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 12px;
`
